import { Link, useNavigate, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { useAuth } from "@/hooks/useAuth";
import { logActivity } from "@/lib/activityLog";
import MarcaEntreElas from "@/components/feature/MarcaEntreElas";

export default function CommunityNavbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const { isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Fecha a busca ao navegar
  useEffect(() => {
    setSearchOpen(false);
    setSearchTerm("");
  }, [location.pathname]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = searchTerm.trim();
    if (!trimmed) return;
    navigate(`/comunidade?q=${encodeURIComponent(trimmed)}`);
    setSearchOpen(false);
    setSearchTerm("");
    logActivity("search", { query: trimmed });
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md shadow-sm">
      <div className="w-full px-6 md:px-10 lg:px-16">
        <div className="flex items-center justify-between h-16 md:h-20">
          <MarcaEntreElas />

          <div className="hidden md:flex items-center gap-6">
            <Link
              to="/"
              className="text-sm font-body text-brown tracking-wide uppercase transition-colors duration-300 hover:text-primary whitespace-nowrap"
            >
              Início
            </Link>
            <Link
              to="/meu-perfil"
              className="text-sm font-body text-brown tracking-wide uppercase transition-colors duration-300 hover:text-primary whitespace-nowrap"
            >
              Meu Perfil
            </Link>
            {isAuthenticated && (
              <button
                onClick={logout}
                className="text-sm font-body text-brown/50 tracking-wide uppercase transition-colors duration-300 hover:text-red-500 whitespace-nowrap cursor-pointer"
              >
                Sair
              </button>
            )}
            <Link
              to="/cadastro"
              className="text-sm font-body text-brown tracking-wide uppercase transition-colors duration-300 hover:text-primary whitespace-nowrap"
            >
              Cadastro
            </Link>

            {/* Search toggle button */}
            <button
              onClick={() => { setSearchOpen(!searchOpen); setSearchTerm(""); }}
              className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-beige transition-colors cursor-pointer"
              title="Buscar membros"
            >
              <i className={`ri-${searchOpen ? "close" : "search"}-line text-brown text-lg`} />
            </button>
          </div>

          {/* Mobile hamburger + search */}
          <div className="md:hidden flex items-center gap-2">
            <button
              onClick={() => { setSearchOpen(!searchOpen); setSearchTerm(""); setMobileOpen(false); }}
              className="w-10 h-10 flex items-center justify-center cursor-pointer"
              aria-label="Buscar"
            >
              <i className={`ri-${searchOpen ? "close" : "search"}-line text-xl text-brown`} />
            </button>
            <button
              className="w-10 h-10 flex items-center justify-center"
              onClick={() => setMobileOpen(!mobileOpen)}
              aria-label="Menu"
            >
              <i className={`ri-${mobileOpen ? "close" : "menu"}-line text-xl text-brown`} />
            </button>
          </div>
        </div>

        {/* Search bar expandida */}
        {searchOpen && (
          <div className="pb-3 md:pb-4">
            <form onSubmit={handleSearch} className="flex items-center gap-2 w-full md:max-w-md md:ml-auto">
              <div className="relative flex-1">
                <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-brown/30 text-sm" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Nome, cidade, profissão, serviços..."
                  autoFocus
                  className="w-full pl-11 pr-4 py-2.5 rounded-full border border-nude bg-white font-body text-sm text-brown placeholder-brown/30 focus:outline-none focus:border-primary transition-colors"
                />
              </div>
              <button
                type="submit"
                className="shrink-0 px-5 py-2.5 rounded-full bg-primary text-white font-body text-sm hover:bg-primary-dark transition-colors whitespace-nowrap cursor-pointer"
              >
                Buscar
              </button>
            </form>
          </div>
        )}
      </div>

      {mobileOpen && (
        <div className="md:hidden bg-white/98 backdrop-blur-md border-t border-beige">
          <div className="flex flex-col px-6 py-4 gap-3">
            <Link
              to="/"
              onClick={() => setMobileOpen(false)}
              className="text-left text-sm font-body text-brown tracking-wide uppercase py-2 hover:text-primary transition-colors whitespace-nowrap"
            >
              Início
            </Link>
            <Link
              to="/meu-perfil"
              onClick={() => setMobileOpen(false)}
              className="text-left text-sm font-body text-brown tracking-wide uppercase py-2 hover:text-primary transition-colors whitespace-nowrap"
            >
              Meu Perfil
            </Link>
            {isAuthenticated && (
              <button
                onClick={() => { logout(); setMobileOpen(false); }}
                className="text-left text-sm font-body text-brown/50 tracking-wide uppercase py-2 hover:text-red-500 transition-colors whitespace-nowrap cursor-pointer"
              >
                Sair
              </button>
            )}
            <Link
              to="/cadastro"
              onClick={() => setMobileOpen(false)}
              className="text-left text-sm font-body text-brown tracking-wide uppercase py-2 hover:text-primary transition-colors whitespace-nowrap"
            >
              Cadastro
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
}